"use client";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { IntegrationOwnershipBadge } from "@/components/shared/integration-ownership-badge";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Boxes, ChevronDown, Copy } from "lucide-react";
import { toast } from "sonner";
import { IntegrationMetaBadge } from "@/components/integrations/ui/integration-meta-badge";
import { IntegrationStatusBadge } from "@/components/integrations/ui/integration-status-badge";
import { useIntegrationCatalogMap } from "@/hooks/use-integration-catalog-map";

interface ConnectedAppsActionsProps {
    appIntegrations: ConnectedIntegration[];
    customApps?: ConnectedCustomApp[];
    /** When true, omit the gray "Available Actions for …" lines (caller provides headings). */
    hideSectionTitles?: boolean;
    /** Merged onto the root wrapper (defaults include top margin). */
    className?: string;
}

function ActionsCollapsible({
    actions,
}: {
    actions: AppAction[];
}) {
    if (!actions.length) return null;
    return (
        <Collapsible className="w-full">
            <CollapsibleTrigger className="flex items-center justify-between w-full p-3 text-sm font-medium bg-muted/50 rounded-md hover:bg-muted/70 transition-colors">
                <span>Available Actions</span>
                <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="max-h-72 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-3">
                        {actions.map((action) => (
                            <div
                                key={action.enum || action.display_name}
                                className="flex items-start gap-2 p-2 rounded-md bg-muted/50"
                            >
                                <div className="min-w-[16px] mt-1">
                                    <div className="w-2 h-2 rounded-full bg-primary" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium">
                                        {action.display_name}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {action.description}
                                    </p>
                                </div>
                                {action.enum && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                type="button"
                                                size="icon"
                                                variant="ghost"
                                                className="rounded-full border-muted hover:bg-muted transition h-6 w-6 p-0 mt-1"
                                                onClick={async () => {
                                                    await navigator.clipboard.writeText(
                                                        action.enum as string
                                                    );
                                                    toast.success(
                                                        "Action slug copied to clipboard"
                                                    );
                                                }}
                                            >
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent
                                            side="top"
                                            align="end"
                                            className="text-[10px]"
                                        >
                                            {action.enum}
                                        </TooltipContent>
                                    </Tooltip>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}

export function ConnectedAppsActions({
    appIntegrations,
    customApps = [],
    hideSectionTitles = false,
    className,
}: ConnectedAppsActionsProps) {
    const hasIntegrations = appIntegrations && appIntegrations.length > 0;
    const hasCustom = customApps && customApps.length > 0;
    const { resolveIntegrationMeta, resolveIntegrationStatus } =
        useIntegrationCatalogMap(hasIntegrations);

    if (!hasIntegrations && !hasCustom) {
        return null;
    }

    return (
        <div className={cn("mt-8 space-y-6", className)}>
            {hasIntegrations && (
                <>
                    {!hideSectionTitles && (
                        <div className="text-sm font-medium text-muted-foreground mb-4">
                            Available Actions for Connected Apps
                        </div>
                    )}
                    <div className="grid grid-cols-1 gap-6">
                        {appIntegrations.map((integration, index) => {
                            const { alias, reference } =
                                resolveIntegrationMeta(integration);
                            return (
                            <div
                                key={`integration-${integration.integration_id || integration.name}-${index}`}
                                className="border rounded-lg p-4 space-y-4 bg-card"
                            >
                                <div className="flex items-start gap-3">
                                    {integration.app_logo_url && (
                                        <img
                                            src={integration.app_logo_url}
                                            alt={integration.name}
                                            width={24}
                                            height={24}
                                            className="mt-0.5 object-contain"
                                        />
                                    )}
                                    <div className="min-w-0 flex-1 space-y-1">
                                        <div className="flex min-w-0 items-center gap-2">
                                            <h4 className="truncate font-medium text-base">
                                                {integration.name}
                                            </h4>
                                            {alias ? (
                                                <IntegrationMetaBadge
                                                    variant="alias"
                                                    title={alias}
                                                    className="max-w-[8rem] shrink-0 px-1.5 py-0 text-[10px] leading-4"
                                                >
                                                    {alias}
                                                </IntegrationMetaBadge>
                                            ) : null}
                                        </div>
                                        {reference ? (
                                            <p className="text-sm text-muted-foreground">
                                                {reference}
                                            </p>
                                        ) : null}
                                        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                                            <IntegrationStatusBadge
                                                status={resolveIntegrationStatus(integration)}
                                                className="px-2 py-0.5 text-[10px] font-medium"
                                            />
                                            <IntegrationOwnershipBadge
                                                owner={integration.owner}
                                                visibility={integration.visibility}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {integration.actions_available.length > 0 && (
                                    <ActionsCollapsible actions={integration.actions_available} />
                                )}
                            </div>
                            );
                        })}
                    </div>
                </>
            )}

            {hasCustom && (
                <div className="space-y-6">
                    {!hideSectionTitles && (
                        <div className="text-sm font-medium text-muted-foreground mb-4">
                            Available Actions for Custom Apps
                        </div>
                    )}
                    <div className="grid grid-cols-1 gap-6">
                        {customApps.map((app, index) => (
                            <div
                                key={`custom-app-${app.custom_app_id || app.name}-${index}`}
                                className="border rounded-lg p-4 space-y-4 bg-card"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="flex h-6 w-6 shrink-0 items-center justify-center text-muted-foreground">
                                        <Boxes className="h-6 w-6" aria-hidden />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <h4 className="font-medium text-base">{app.name}</h4>
                                        <p className="text-sm text-muted-foreground">
                                            {app.description || app.type}
                                        </p>
                                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                            <IntegrationStatusBadge
                                                status={app.status}
                                                className="px-2 py-0.5 text-[10px] font-medium"
                                            />
                                            <IntegrationOwnershipBadge
                                                owner={app.owner}
                                                visibility={app.visibility}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

